/**
 * What `/post-purchase` tells the conversion pixels, and when it asks at all.
 *
 * Both halves are server-sourced and both fail closed. `amount_usd` is revenue
 * **at this moment** — null while trialing, null before the subscription is
 * active — and `trial_started` comes off Stripe's own status rather than off
 * whatever trial the paywall offered. Reporting a fabricated value is the bug
 * the cookie-tracker audit set out to fix; under-counting is the safe
 * direction, so every "we do not know" path here resolves to no pixels at all.
 *
 * @see docs/projects/active/audit-cookie-trackers.md
 */

export type PurchasePixelFields = {
  readonly amountUsd: number | null;
  readonly currency: string | null;
  readonly trialStarted: boolean;
  readonly purchaseEventId: string | null;
};

/** No Purchase pixel, no trial pixel. The safe default for every unknown. */
export const NO_PURCHASE_PIXELS: PurchasePixelFields = {
  amountUsd: null,
  currency: null,
  trialStarted: false,
  purchaseEventId: null,
};

/**
 * Whether this page load is a return from Stripe worth confirming.
 *
 * `POST /billing/confirm` is a write — it syncs from Stripe and marks the user
 * as having paid — so a ghost URL, a bookmark or a declined redirect must not
 * trigger it.
 */
export const arrivedFromStripe = (deps: {
  readonly paymentIntent?: string;
  readonly setupIntent?: string;
  readonly redirectStatus?: 'succeeded' | 'failed' | 'pending';
}): boolean =>
  Boolean(deps.paymentIntent ?? deps.setupIntent) &&
  deps.redirectStatus !== 'failed';

/** The two pixel signals, off a `POST /billing/confirm` response. */
export const purchasePixelsFrom = (confirm: {
  readonly amount_usd: number | null;
  readonly currency: string | null;
  readonly trial_started: boolean;
  readonly purchase_event_id?: string | null;
}): PurchasePixelFields => ({
  amountUsd: confirm.amount_usd,
  currency: confirm.currency,
  trialStarted: confirm.trial_started,
  purchaseEventId: confirm.purchase_event_id ?? null,
});
